import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  capitalizeFirstLetter,
  Field,
  FieldTypes,
  Inscription,
  Option,
  Position,
  ToggleButtonGroup,
  useResponsive,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toNumber } from 'lodash';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { DATE_INVALID_ALTERNATIVE_TEXT, DEFAULT_DATE_FORMAT, REQUIRED_TEXT } from '@/common/constants';
import regexp from '@/common/regexp';
import useEmployerTerminationBanner from '@/modules/core/hooks/useEmployerTerminationBanner';
import { Service } from '@/modules/transaction/queries/useServices.query';
import theme from '@/styles/theme';
import { getFullAddress } from '@/utils';
import getYearsOldTitle from '@/utils/modifiers/getYearsOldTitle';

import { FormField } from '../../ReimburseMe/ReimburseMe.types';
import { OcrWarnings, OcrWarningType, ParsedFileByOcr } from '../FileScan/fileScan.types';
import { useClaimInfoStore } from '../store/useClaimInfo.store';

import { OcrFieldsWarningErrorsTracker } from './ClaimWarningBanner/OcrFieldsWarningErrorsTracker';
// import { RetailOutOfCoverageDateErrorBanner } from './ClaimWarningBanner/RetailOutOfCoverageDateErrorBanner';
import { OutOfCoverageDateWarningBanner } from './ClaimWarningBanner/OutOfCoverageDateWarningBanner';
import { useOcrWarningsStore } from './ClaimWarningBanner/store';
import { findOptionValueByKey } from './hooks/findOption';
import { useClaimInfoGroups } from './hooks/useClaimInfoGroups';
import { useClaimInfoFieldsFilterStore } from './stores/useClaimInfoFieldsFilter.store';
import AccountsToPayMessage from './AccountsToPayMessage';
import AddAddress from './AddAddress';
import { Patient } from './usePatients.query';
import { Provider } from './useProviders.query';

const TITLE_ME = 'Me';

export enum ClaimInfoFields {
  account = 'account',
  amount = 'amount',
  invoice = 'invoice',
  patient = 'patient',
  provider = 'provider',
  fullAddress = 'fullAddress',
  service = 'service',
  serviceDate = 'serviceDate',
  employeeNotes = 'employeeNotes',
}

const DATE_IS_INCORRECT = 'Provided Date of Service is incorrect';

export const getMissingFieldsErrors = (warnings?: OcrWarnings, showErrors = true) => {
  if (!warnings || !showErrors) return {};
  const providerMissingError = warnings[OcrWarningType.MISSING].find(
    (warning) => warning.fieldName === ClaimInfoFields.provider
  )
    ? REQUIRED_TEXT
    : undefined;
  const serviceMissingError = warnings[OcrWarningType.MISSING].find(
    (warning) => warning.fieldName === ClaimInfoFields.service
  )
    ? REQUIRED_TEXT
    : undefined;
  const serviceDateMissingError = warnings[OcrWarningType.MISSING].find(
    (warning) => warning.fieldName === ClaimInfoFields.serviceDate
  )
    ? REQUIRED_TEXT
    : undefined;
  const amountMissingError = warnings[OcrWarningType.MISSING].find(
    (warning) => warning.fieldName === ClaimInfoFields.amount
  )
    ? REQUIRED_TEXT
    : undefined;
  const patientMissingError = warnings[OcrWarningType.MISSING].find(
    (warning) => warning.fieldName === ClaimInfoFields.patient
  )
    ? REQUIRED_TEXT
    : undefined;

  return {
    providerMissingError,
    serviceMissingError,
    serviceDateMissingError,
    amountMissingError,
    patientMissingError,
  };
};

const getAddButtonStyles = (isActive?: boolean) => {
  if (isActive) {
    return {
      borderColor: theme.colors.accentActive,
    };
  }
};

const getProviderOptionValue = (patient: Patient) => {
  const relationship = capitalizeFirstLetter(patient?.relationshipType || '').replace(
    regexp.DASH_SYMBOL,
    ' '
  );

  if (!relationship || relationship === TITLE_ME) {
    return {
      title: `${patient.fullName} ${relationship ? `(${relationship})` : ''}`.trim(),
    };
  }

  return {
    title: patient.fullName,
    description: (
      <Box data-testId={`patient_${patient.id}_years_old`}>
        <Inscription color="textSecondary" lineHeight="18px" size="small" weight={500}>
          {`${relationship}, ${getYearsOldTitle(patient.birthOn)}`}
        </Inscription>
      </Box>
    ),
  };
};

const isDateInFuture = (date?: string) => dayjs(date).valueOf() > dayjs().valueOf();

interface Props {
  providers: Provider[];
  services: Service[];
  patients: Patient[];
  addPatient: () => void;
  resetCreatedPatientId: () => void;
  resetProvider: (id?: string) => void;
  scannedDocument?: ParsedFileByOcr;
  createdProviderId?: string;
  isBillPayMode?: boolean;
  addProvider?: () => void;
  initEditing?: {
    onEdit?: () => void;
    isHighlighted?: boolean;
    disabled?: boolean;
  };
  isCoveragePeriodActive: boolean;
  hasValidationAccountOrdersError: boolean;
  isServicesLoading: boolean;
  isAddProviderModalVisible: boolean;
  isAddPatientModalActive: boolean;
  accountNames?: string[];
}

const useClaimInfoFields = ({
  providers,
  services,
  patients,
  addPatient,
  resetCreatedPatientId,
  resetProvider,
  scannedDocument,
  createdProviderId,
  isBillPayMode,
  addProvider,
  initEditing,
  isCoveragePeriodActive,
  isServicesLoading,
  hasValidationAccountOrdersError,
  isAddProviderModalVisible,
  isAddPatientModalActive,
  accountNames,
}: Props) => {
  const { isMobile } = useResponsive();
  const { handleSetValues, handleSetProviderId, handleSetService, values: claimInfo } = useClaimInfoStore();
  const [scannedServiceId, setScannedServiceId] = useState<string>();
  const { setFilterValues, setLastEditedField, resetFilterValues } = useClaimInfoFieldsFilterStore();
  const fieldsWarnings = useOcrWarningsStore((state) => state.fieldsOcrWarnings);
  const warnings = useOcrWarningsStore((state) => state.ocrWarnings);
  const setFieldsOcrWarning = useOcrWarningsStore((state) => state.handleSetFieldsOcrWarningByType);
  const removeFieldsOcrWarning = useOcrWarningsStore((state) => state.handleRemoveFieldsOcrWarningByType);
  const { provider: providerId } = claimInfo;
  const { t } = useTranslation();
  const [cleaningInitiator, setCleaningInitiator] = useState(false);
  const selectedProvider = useMemo(
    () => providers.find((provider) => provider?.id?.toString() === providerId),
    [providerId, providers]
  );

  const isRetailOutOfCoverageError = useMemo(() => {
    if (!scannedDocument || !scannedDocument?.warnings) return false;
    return fieldsWarnings[OcrWarningType.RETAIL_OUT_OF_COVERAGE_DATE].length;
  }, [scannedDocument, fieldsWarnings]);
  const isOutOfCoverageDateError = useMemo(() => {
    if (!scannedDocument || !scannedDocument?.warnings) return false;
    return fieldsWarnings[OcrWarningType.OUT_OF_COVERAGE_DATE].length;
  }, [scannedDocument, fieldsWarnings]);

  const {
    providerMissingError,
    serviceMissingError,
    serviceDateMissingError,
    amountMissingError,
    patientMissingError,
  } = getMissingFieldsErrors(fieldsWarnings, !!scannedDocument);

  const dateFieldManualError = useMemo(() => {
    if (isServicesLoading) {
      return false;
    }

    if (isRetailOutOfCoverageError || isOutOfCoverageDateError) {
      return t(DATE_INVALID_ALTERNATIVE_TEXT);
    }
    return serviceDateMissingError;
  }, [isOutOfCoverageDateError, isRetailOutOfCoverageError, serviceDateMissingError, t, isServicesLoading]);

  const isServicesExist = useMemo(() => services.length > 0, [services]);

  const foundScannedServiceId = useMemo(
    () =>
      (services.find((service) => service.name === scannedDocument?.serviceDescription)?.id || '').toString(),
    [scannedDocument, services]
  );

  useEffect(() => {
    if (foundScannedServiceId && !scannedServiceId) {
      setScannedServiceId(foundScannedServiceId);
      handleSetService(foundScannedServiceId);
    }
  }, [scannedServiceId, foundScannedServiceId, handleSetService]);

  useEffect(() => {
    if (createdProviderId) {
      handleSetProviderId(createdProviderId);
    }
  }, [createdProviderId, handleSetProviderId]);

  const fullAddress = useMemo(() => getFullAddress(selectedProvider?.address), [selectedProvider]);
  const fullAddressFieldColor = useMemo(() => {
    if (!isBillPayMode) {
      return 'textTitle';
    }

    if (selectedProvider && !fullAddress) {
      return 'error';
    }

    if (fullAddress) {
      return 'textTitle';
    }

    return 'textSecondary';
  }, [fullAddress, selectedProvider, isBillPayMode]);
  const completeProviderId = useMemo(
    () => createdProviderId || selectedProvider?.id,
    [createdProviderId, selectedProvider]
  );
  const providerOptions = useMemo<Option[]>(
    () =>
      providers.map((provider) => ({
        key: `${provider.id}`,
        value: provider.name || '',
        title: provider.name,
      })),
    [providers]
  );
  const patientsOptions = useMemo<Option[]>(
    () =>
      patients.map((patient) => ({
        key: `${patient.id}`,
        value: patient.fullName,
        ...getProviderOptionValue(patient),
      })),
    [patients]
  );
  const servicesOptions = useMemo<Option[]>(
    () =>
      services.map((service) => ({
        key: service.id,
        value: service.name,
        title: service.name,
      })),
    [services]
  );

  const { providerGroups, patientGroups, suggestedAmountOptions, serviceGroups, serviceDatesGroups } =
    useClaimInfoGroups({
      providerOptions,
      servicesOptions,
      patientsOptions,
      scannedDocument,
      serviceDate: claimInfo.serviceDate,
    });
  const completeProviderName = useMemo(() => {
    if (!completeProviderId) return '';

    return providers.find((provider) => provider.id === completeProviderId)?.name || '';
  }, [completeProviderId, providers]);
  const handleOnChangeProvider = useCallback(
    (value?: string) => {
      const id = createdProviderId || value;

      if (!id) return;

      if (createdProviderId && selectedProvider?.id === createdProviderId) {
        resetProvider(id);
      }

      if (id !== providerId) {
        handleSetProviderId(id);
      }
    },
    [createdProviderId, providerId, selectedProvider, handleSetProviderId, resetProvider]
  );
  const onSearch = useCallback(
    (searchValue: string) => {
      if ((!searchValue && selectedProvider) || (selectedProvider && searchValue !== selectedProvider.name)) {
        handleSetProviderId();
      }
    },
    [selectedProvider, handleSetProviderId]
  );

  const resetScannedServiceId = useCallback(() => {
    handleSetService('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { isDisabledDueToTermination } = useEmployerTerminationBanner();
  const addProviderButtonStyles = useMemo(
    () => getAddButtonStyles(isAddProviderModalVisible),
    [isAddProviderModalVisible]
  );
  const addPatientButtonStyles = useMemo(
    () => getAddButtonStyles(isAddPatientModalActive),
    [isAddPatientModalActive]
  );

  /* eslint-disable-next-line react-hooks/exhaustive-deps */
  useEffect(() => setCleaningInitiator(!cleaningInitiator), [completeProviderName]);

  useEffect(() => {
    if (services.length && (isRetailOutOfCoverageError || isOutOfCoverageDateError)) {
      removeFieldsOcrWarning(OcrWarningType.OUT_OF_COVERAGE_DATE, ClaimInfoFields.serviceDate);
      removeFieldsOcrWarning(OcrWarningType.RETAIL_OUT_OF_COVERAGE_DATE, ClaimInfoFields.serviceDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services.length]);

  useEffect(() => {
    OcrFieldsWarningErrorsTracker(claimInfo, warnings, setFieldsOcrWarning, removeFieldsOcrWarning);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claimInfo]);

  useEffect(() => {
    return () => {
      resetFilterValues();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [
    ...(isBillPayMode
      ? [
          {
            // cleaningInitiator,
            onSearch,
            name: ClaimInfoFields.provider,
            type: FieldTypes.AutocompleteDropdown,
            label: t('Provider/Merchant'),
            placeholder: t('Select Provider/Merchant'),
            testId: 'provider_merchant',
            notCompletelyFilledTextError: t('Please confirm creating Provider/Merchant'),
            validator: yup.string().required(REQUIRED_TEXT),
            manualError: providerMissingError,
            options: providerGroups,
            showRequireIcon: true,
            isSearchMode: true,
            completeValue: claimInfo?.provider,
            resetCompleteValue: resetProvider,
            onClear: handleSetProviderId,
            add: addProvider,
            initEditing,
            onChange: handleOnChangeProvider,
            ...(selectedProvider && !fullAddress ? { className: 'error-border' } : {}),
            optionsPlaceholder: t('There are no providers in the list. Please enter your own'),
            disableFiltering: true,
            readOnly: true,
            addButtonStyles: addProviderButtonStyles,
            onSelect: (provider: string) => {
              if (provider) {
                handleSetValues({ isEdited: true });
              }
            },
          },
        ]
      : [
          {
            onSearch,
            name: ClaimInfoFields.provider,
            type: FieldTypes.AutocompleteDropdown,
            label: t('Provider/Merchant'),
            testId: 'provider_merchant',
            placeholder: t('Select Provider/Merchant'),
            notCompletelyFilledTextError: t('Please confirm creating Provider/Merchant'),
            validator: yup.string().required(REQUIRED_TEXT),
            manualError: providerMissingError,
            options: providerGroups,
            showRequireIcon: true,
            isSearchMode: true,
            completeValue: claimInfo?.provider,
            defaultText: scannedDocument?.providerName,
            onClear: handleSetProviderId,
            resetCompleteValue: resetProvider,
            add: addProvider,
            onChange: handleOnChangeProvider,
            optionsPlaceholder: t('There are no providers in the list. Please enter your own'),
            disableFiltering: true,
            readOnly: true,
            addButtonStyles: addProviderButtonStyles,
            onSelect: (provider: string) => {
              if (provider) {
                handleSetValues({ isEdited: true });
              }
            },
          },
        ]),
    ...(isBillPayMode
      ? [
          {
            name: ClaimInfoFields.fullAddress,
            type: FieldTypes.Node,
            label: t('Address'),
            testId: 'address',
            showRequireIcon: true,
            isThinMode: true,
            value: (
              <Inscription lineHeight="20px" color={fullAddressFieldColor}>
                {selectedProvider
                  ? fullAddress || t('No address')
                  : t('Select a Provider to display its address')}
              </Inscription>
            ),
          },
        ]
      : []),
    ...(isBillPayMode && selectedProvider && !fullAddress
      ? [
          {
            type: FieldTypes.Node,
            name: 'addAddress',
            testId: 'add-address',
            value: <AddAddress onClick={initEditing?.onEdit} />,
          },
        ]
      : []),
    {
      name: ClaimInfoFields.patient,
      type: FieldTypes.AutocompleteDropdown,
      add: addPatient,
      label: t('Family member'),
      testId: 'family-member',
      placeholder: t('Select family member'),
      options: patientGroups,
      validator: yup.string().required(REQUIRED_TEXT),
      manualError: patientMissingError,
      showRequireIcon: true,
      isSearchMode: true,
      completeValue: claimInfo.patient,
      resetCompleteValue: resetCreatedPatientId,
      onChange: (patient: string) => handleSetValues({ patient }),
      onSelect: (patient: string) => {
        setFilterValues({
          patient: findOptionValueByKey(patientsOptions, patient),
          serviceDates: undefined,
          service: undefined,
        });
        if (patient || (claimInfo.patient && !patient)) {
          setLastEditedField(FormField.patient);
          handleSetValues({ isEdited: true });
        }
      },
      disableFiltering: true,
      readOnly: true,
      addButtonStyles: addPatientButtonStyles,
      disableAddButton: isDisabledDueToTermination,
      isAddButtonWithHint: isDisabledDueToTermination,
      addButtonHintText: 'Terminated Organization cannot add dependents',
    },
    {
      name: ClaimInfoFields.serviceDate,
      type: FieldTypes.Date,
      label: t('Date of service'),
      testId: 'date-of-service',
      isManualDateInput: true,
      inputWrapCssClass: isMobile ? undefined : 'small-input',
      placeholder: t('Date of service'),
      validator: VALIDATORS.DATE_FORMAT.required(REQUIRED_TEXT).test(
        'date',
        DATE_IS_INCORRECT,
        (date) => !isDateInFuture(date)
      ),
      manualError: dateFieldManualError,
      showRequireIcon: true,
      isCalendar: true,
      pickerSide: Position.LEFT,
      value:
        scannedDocument?.serviceDate && !isDateInFuture(scannedDocument?.serviceDate)
          ? dayjs(scannedDocument.serviceDate).format(DEFAULT_DATE_FORMAT)
          : '',
      ...(isCoveragePeriodActive ? {} : { className: 'error-border' }),
      completeValue: claimInfo.serviceDate,
      onChange: (serviceDate: string) => {
        handleSetValues({ serviceDate });
      },
      onSelect: (serviceDate: string) => {
        setFilterValues({
          serviceDates: serviceDate,
          service: undefined,
        });
        if (serviceDate || (claimInfo.serviceDate && !serviceDate)) {
          setLastEditedField(FormField.serviceDate);
          handleSetValues({ isEdited: true });
        }
      },
      maxDate: dayjs(),

      ...(isMobile
        ? {}
        : {
            // TODO: change it to options
            highlightedDatesTitle: serviceDatesGroups?.length
              ? 'Choose one of the suggested options or select any from the calendar'
              : 'There are no suggested options. Please select the most appropriate from the calendar',
            highlightedDates: serviceDatesGroups,
          }),
    },
    // ...isRetailOutOfCoverageError ? [{
    //   name: 'errorRetailOutOfCoverageDate',
    //   type: FieldTypes.Node,
    //   value: <RetailOutOfCoverageDateErrorBanner
    //     serviceType={scannedDocument?.serviceDescription}
    //     lastDateToIncurClaim={scannedDocument?.serviceDate}
    //     planLastDateToIncurClaim={scannedDocument?.serviceDate}
    //   />,
    // }] : [],
    ...(dateFieldManualError
      ? [
          {
            testId: 'errorOutOfCoverageDate',
            name: 'errorOutOfCoverageDate',
            type: FieldTypes.Node,
            value: <OutOfCoverageDateWarningBanner />,
          },
        ]
      : []),
    {
      name: ClaimInfoFields.amount,
      type: FieldTypes.Currency,
      label: t('Amount'),
      testId: 'amount',
      placeholder: t('Amount'),
      inputWrapCssClass: 'small-input',
      validator: yup
        .string()
        .required(REQUIRED_TEXT)
        .test('', REQUIRED_TEXT, (amount) => {
          if (!amount) return false;
          return toNumber(amount) > 0;
        }),
      manualError: amountMissingError,
      showRequireIcon: true,
      value: scannedDocument?.amount,
      completeValue: claimInfo.amount,
      onChange: (amount: string) => {
        handleSetValues({ amount });
        if (amount && claimInfo.amount && amount !== claimInfo.amount) {
          setLastEditedField(FormField.amount);
          handleSetValues({ isEdited: true });
        }
      },
    },
    ...(suggestedAmountOptions?.length
      ? [
          {
            name: 'amountOptions',
            type: FieldTypes.Node,
            testId: 'amount-options',
            label: '',
            value: (
              <Box width="100%">
                <ToggleButtonGroup
                  value={claimInfo?.amount}
                  options={suggestedAmountOptions}
                  onChange={(amount) => {
                    handleSetValues({ amount: amount as string });
                    if (amount && claimInfo.amount && amount !== claimInfo.amount) {
                      setLastEditedField(FormField.amount);
                      handleSetValues({ isEdited: true });
                    }
                  }}
                />
              </Box>
            ),
          },
        ]
      : []),
    {
      name: ClaimInfoFields.service,
      type: FieldTypes.AutocompleteDropdown,
      label: t('Service description'),
      testId: 'service_description',
      placeholder: t('Select service'),
      validator: yup.string().required(REQUIRED_TEXT),
      manualError: isServicesLoading ? undefined : serviceMissingError,
      maxOptionsCount: 1000,
      completeValue: isServicesExist ? claimInfo.service : undefined,
      options: isServicesLoading ? [] : serviceGroups,
      showRequireIcon: true,
      onChange: (service: string) => {
        if (isServicesLoading) {
          return;
        }

        handleSetService(service);
      },
      onSelect: (service: string) => {
        setFilterValues({ service: findOptionValueByKey(servicesOptions, service) });
        if (service || (claimInfo.service && !service)) {
          setLastEditedField(FormField.service);
          handleSetValues({ isEdited: true });
        }
      },
      isSearchMode: true,
      isLoading: isServicesLoading,
      resetCompleteValue: resetScannedServiceId,
      optionsPlaceholder: !claimInfo?.serviceDate
        ? t('Select the date of service first to view and choose relevant services')
        : undefined,
      ...(hasValidationAccountOrdersError ? { className: 'error-border' } : {}),
      disableFiltering: true,
    },
    ...(accountNames?.length
      ? [
          {
            name: 'accountToPayMessage',
            testId: 'accountToPayMessage',
            type: FieldTypes.Node,
            labelStyle: { display: 'none' },
            value: (
              <Box width={{ width: '100%', max: '560px' }}>
                <AccountsToPayMessage accountNames={accountNames} />
              </Box>
            ),
          },
        ]
      : []),
    {
      name: ClaimInfoFields.account,
      type: FieldTypes.Text,
      label: t('Account #'),
      testId: 'account-number',
      placeholder: t('Enter account #'),
      validator: yup.string(),
      value: scannedDocument?.accountId,
    },
    {
      name: ClaimInfoFields.invoice,
      type: FieldTypes.Text,
      label: t('Invoice #'),
      testId: 'invoice-number',
      placeholder: t('Enter invoice #'),
      validator: yup.string(),
      value: scannedDocument?.invoiceId,
    },
    {
      name: ClaimInfoFields.employeeNotes,
      type: FieldTypes.TextArea,
      label: t('Notes'),
      testId: 'notes',
      maxLength: 200,
      placeholder: t('Add any helpful notes for the claim processor'),
      style: { alignItems: 'flex-start' },
      hideTip: true,
    },
  ] as Field[];
};

export default useClaimInfoFields;
